import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Download, Upload, Plus, RefreshCw, Pencil, Trash2 } from "lucide-react";

const UNIT = { number: "Number", percent: "%", yesno: "Yes/No" };
const EMPTY = { name: "", level: "output", milestone_id: null, unit: "number", baseline: null, target: null, period_targets: {},
  frequency: "monthly", means_of_verification: "", owner_id: null, direction: "higher" };

const fmt = (v, unit) => (v == null ? "—" : unit === "yesno" ? (v >= 1 ? "Yes" : "No") : unit === "percent" ? `${v}%` : Number(v).toLocaleString("en-IN"));

const ptText = (pt) => Object.entries(pt || {}).map(([k, v]) => `${k}=${v}`).join("; ");
const ptParse = (s) => Object.fromEntries((s || "").split(";").map(x => x.split("=")).filter(x => x.length === 2 && x[0].trim())
  .map(([k, v]) => [k.trim(), Number(v)]));

function IndicatorDialog({ ind, milestones, members, onClose, onSaved, projectId }) {
  const [f, setF] = useState(ind);
  const [pt, setPt] = useState(ptText(ind.period_targets));
  const [busy, setBusy] = useState(false);
  const num = (v) => (v === "" || v == null ? null : Number(v));
  const save = async () => {
    if (!f.name.trim()) { toast.error("Name is required"); return; }
    setBusy(true);
    const body = { ...f, baseline: num(f.baseline), target: num(f.target), period_targets: ptParse(pt) };
    ["id", "project_id", "cumulative_verified", "pending_verification", "last_updated", "history", "owner_name", "milestone_code",
      "milestone_title", "source", "created_at", "created_by", "updated_at", "updated_by"].forEach(k => delete body[k]);
    try {
      if (ind.id) await api.patch(`/mel/indicators/${ind.id}`, body);
      else await api.post(`/mel/projects/${projectId}/indicators`, body);
      toast.success("Indicator saved"); onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };
  const sel = (k, opts, testId) => (
    <Select value={f[k] ?? "none"} onValueChange={(v) => setF({ ...f, [k]: v === "none" ? null : v })}>
      <SelectTrigger data-testid={testId}><SelectValue /></SelectTrigger>
      <SelectContent>{opts.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
    </Select>
  );
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="indicator-dialog">
        <DialogHeader><DialogTitle>{ind.id ? "Edit indicator" : "New indicator"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Indicator *</Label><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="ind-name" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Level</Label>{sel("level", [["outcome", "Outcome"], ["output", "Output"], ["activity", "Activity"]], "ind-level")}</div>
            <div><Label>Logframe item</Label>{sel("milestone_id", [["none", "— none —"], ...milestones.map(m => [m.id, `${m.code || ""} ${m.title}`.trim().slice(0, 70)])], "ind-milestone")}</div>
            <div><Label>Unit</Label>{sel("unit", [["number", "Number"], ["percent", "%"], ["yesno", "Yes/No"]], "ind-unit")}</div>
            <div><Label>Frequency</Label>{sel("frequency", [["monthly", "Monthly"], ["quarterly", "Quarterly"], ["one-time", "One-time"]], "ind-frequency")}</div>
            <div><Label>Baseline</Label><Input type="number" value={f.baseline ?? ""} onChange={(e) => setF({ ...f, baseline: e.target.value })} data-testid="ind-baseline" /></div>
            <div><Label>Target (total)</Label><Input type="number" value={f.target ?? ""} onChange={(e) => setF({ ...f, target: e.target.value })} data-testid="ind-target" /></div>
            <div><Label>Direction</Label>{sel("direction", [["higher", "Higher is better"], ["lower", "Lower is better"]], "ind-direction")}</div>
            <div><Label>Owner</Label>{sel("owner_id", [["none", "— none —"], ...members.map(u => [u.id, u.name])], "ind-owner")}</div>
          </div>
          <div><Label>Targets by month / FY <span className="text-slate-400 font-normal">(optional, e.g. 2026-10=100; FY2026-27=1200)</span></Label>
            <Input value={pt} onChange={(e) => setPt(e.target.value)} data-testid="ind-period-targets" /></div>
          <div><Label>Data source / means of verification</Label><Input value={f.means_of_verification || ""} onChange={(e) => setF({ ...f, means_of_verification: e.target.value })} data-testid="ind-mov" /></div>
        </div>
        <DialogFooter><Button disabled={busy} onClick={save} data-testid="ind-save">{busy ? "Saving…" : "Save"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function IndicatorsTab({ projectId, milestones, users, project }) {
  const teamIds = new Set([project?.project_manager_id, ...(project?.team_member_ids || [])].filter(Boolean));
  const members = teamIds.size ? users.filter(u => teamIds.has(u.id)) : users;
  const [data, setData] = useState(null);
  const [edit, setEdit] = useState(null);
  const fileRef = useRef(null);
  const load = () => api.get(`/mel/projects/${projectId}/indicators`).then(r => setData(r.data)).catch(e => toast.error(e.response?.data?.detail || "Could not load indicators"));
  useEffect(() => { load(); }, [projectId]);
  if (!data) return <div className="text-sm text-slate-500 p-4">Loading…</div>;
  const inds = data.indicators;
  const missing = inds.filter(i => i.target == null).length;
  const download = async () => {
    const r = await api.get(`/mel/projects/${projectId}/indicators/template.xlsx`, { responseType: "blob" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(r.data); a.download = `${data.project.project_code || "project"}_indicator_targets.xlsx`; a.click();
  };
  const upload = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try {
      const r = await api.post(`/mel/projects/${projectId}/indicators/upload`, fd);
      toast.success(`Updated ${r.data.updated}, added ${r.data.created}`);
      r.data.errors.slice(0, 5).forEach(x => toast.error(x));
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Upload failed"); }
    e.target.value = "";
  };
  const sync = async () => { const r = await api.post(`/mel/projects/${projectId}/indicators/sync-from-logframe`); toast.success(`${r.data.created} indicator(s) added from logframe`); load(); };
  const del = async (i) => {
    if (!window.confirm(`Delete "${i.name}"?`)) return;
    try { await api.delete(`/mel/indicators/${i.id}`); load(); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const groups = ["outcome", "output", "activity"].map(l => [l, inds.filter(i => i.level === l)]).filter(([, xs]) => xs.length);
  return (
    <div className="space-y-4" data-testid="indicators-tab">
      <div className="flex flex-wrap items-center gap-2">
        <div className="text-sm text-slate-600 mr-auto">{inds.length} indicator(s){missing ? <span className="text-amber-700 font-medium" data-testid="ind-missing-targets"> · {missing} without a target</span> : ""}</div>
        <Button variant="outline" size="sm" onClick={download} data-testid="ind-template-download"><Download className="w-3.5 h-3.5 mr-1" />Excel template</Button>
        {data.can_edit && <>
          <Button variant="outline" size="sm" onClick={() => fileRef.current?.click()} data-testid="ind-template-upload"><Upload className="w-3.5 h-3.5 mr-1" />Upload targets</Button>
          <input ref={fileRef} type="file" accept=".xlsx" className="hidden" onChange={upload} data-testid="ind-upload-input" />
          <Button variant="outline" size="sm" onClick={sync} data-testid="ind-sync"><RefreshCw className="w-3.5 h-3.5 mr-1" />Add from logframe</Button>
          <Button size="sm" onClick={() => setEdit({ ...EMPTY })} data-testid="ind-add"><Plus className="w-3.5 h-3.5 mr-1" />Indicator</Button>
        </>}
      </div>
      {!inds.length && <Card><CardContent className="p-6 text-sm text-slate-500">No indicators yet. Use "Add from logframe" or add one.</CardContent></Card>}
      {groups.map(([lvl, xs]) => (
        <Card key={lvl}><CardContent className="p-0 overflow-x-auto">
          <div className="px-4 py-2 border-b bg-slate-50 text-xs uppercase tracking-wider font-semibold text-slate-600">{{ outcome: "Outcomes", output: "Outputs", activity: "Activities" }[lvl]}</div>
          <table className="w-full text-sm">
            <thead className="text-xs text-slate-500"><tr className="border-b">
              <th className="text-left px-3 py-2">Indicator</th><th className="text-left px-3 py-2">Logframe</th><th className="text-left px-3 py-2">Unit</th>
              <th className="text-right px-3 py-2">Baseline</th><th className="text-right px-3 py-2">Target</th><th className="text-right px-3 py-2">Actual (verified)</th>
              <th className="text-left px-3 py-2">Frequency</th><th className="text-left px-3 py-2">Owner</th><th className="px-3 py-2" />
            </tr></thead>
            <tbody>{xs.map(i => (
              <tr key={i.id} className="border-b border-slate-100 align-top" data-testid={`ind-row-${i.id}`}>
                <td className="px-3 py-2 max-w-md">{i.name}{i.direction === "lower" && <Badge variant="outline" className="ml-1 text-[10px]">lower is better</Badge>}
                  {i.means_of_verification && <div className="text-[11px] text-slate-500">MoV: {i.means_of_verification}</div>}</td>
                <td className="px-3 py-2 font-mono text-xs">{i.milestone_code || "—"}</td>
                <td className="px-3 py-2 text-xs">{UNIT[i.unit]}</td>
                <td className="px-3 py-2 text-right font-mono">{fmt(i.baseline, i.unit)}</td>
                <td className="px-3 py-2 text-right font-mono">{i.target == null ? <span className="text-amber-700 text-xs">not set</span> : fmt(i.target, i.unit)}</td>
                <td className="px-3 py-2 text-right font-mono">{fmt(i.cumulative_verified, i.unit)}{i.pending_verification != null && <div className="text-[11px] text-slate-400">{i.unit === "number" ? "+" : ""}{i.pending_verification} pending</div>}</td>
                <td className="px-3 py-2 text-xs capitalize">{i.frequency}</td>
                <td className="px-3 py-2 text-xs">{i.owner_name || "—"}</td>
                <td className="px-3 py-2 whitespace-nowrap">{data.can_edit && <>
                  <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => setEdit(i)} data-testid={`ind-edit-${i.id}`}><Pencil className="w-3.5 h-3.5" /></Button>
                  <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => del(i)} data-testid={`ind-del-${i.id}`}><Trash2 className="w-3.5 h-3.5" /></Button></>}</td>
              </tr>))}</tbody>
          </table>
        </CardContent></Card>
      ))}
      {edit && <IndicatorDialog ind={edit} projectId={projectId} milestones={milestones} members={members} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); load(); }} />}
    </div>
  );
}
